import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { DELIVERY_DATE_VARIABLE } from "@redotech/redo-model/conversion/delivery-date/format-rate-table-display-text";
import { parseShippingMethodCode } from "@redotech/redo-model/integration/redo-in-shipping/service-code";
import { formatShortDayDate } from "@redotech/redo-web/time";
import { useMemo } from "react";

export function useGetRequestedShippingServicePromisedDeliveryDate({
  fulfillmentGroups,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
}) {
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );

  const parsedServiceCode = useMemo(() => {
    if (fulfillmentGroups.length === 0) {
      return undefined;
    }
    const firstFulfillmentOrderId = Object.keys(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fulfillmentGroups[0].fulfillmentOrderToDeliveryMethod,
    )[0];
    if (!firstFulfillmentOrderId) {
      return undefined;
    }
    const serviceCode =
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fulfillmentGroups[0].fulfillmentOrderToDeliveryMethod?.[
        firstFulfillmentOrderId
      ]?.code;
    if (!serviceCode) {
      return undefined;
    }
    return parseShippingMethodCode(serviceCode) ?? undefined;
  }, [fulfillmentGroups]);

  const requestedShippingServicePromisedDeliveryDate = useMemo(() => {
    if (
      (parsedServiceCode?.type === "legacy-dynamic" ||
        parsedServiceCode?.type === "compact") &&
      parsedServiceCode.parsedInfo.promisedDeliveryDate
    ) {
      return parsedServiceCode.parsedInfo.promisedDeliveryDate.date;
    }
    return undefined;
  }, [parsedServiceCode]);

  const rateTableNameAndDescriptionLoad = useLoad(
    async (signal) => {
      if (
        !parsedServiceCode?.parsedInfo?.rateTableId ||
        !requestedShippingServicePromisedDeliveryDate
      ) {
        return undefined;
      }

      const { description, name } =
        await conversionRpcClient.getRateTableNameAndDescriptionById(
          { rateTableId: parsedServiceCode.parsedInfo.rateTableId },
          { signal },
        );
      return { description, name };
    },
    [
      parsedServiceCode?.parsedInfo?.rateTableId,
      !requestedShippingServicePromisedDeliveryDate,
    ],
  );

  const rateTableDescription = useMemo(() => {
    if (!rateTableNameAndDescriptionLoad.value) {
      return undefined;
    }

    if (
      rateTableNameAndDescriptionLoad.value.description?.includes(
        DELIVERY_DATE_VARIABLE,
      ) &&
      requestedShippingServicePromisedDeliveryDate
    ) {
      return rateTableNameAndDescriptionLoad.value.description.replace(
        DELIVERY_DATE_VARIABLE,
        formatShortDayDate(requestedShippingServicePromisedDeliveryDate),
      );
    } else if (requestedShippingServicePromisedDeliveryDate) {
      return `Arrives by ${formatShortDayDate(requestedShippingServicePromisedDeliveryDate)}`;
    }

    return undefined;
  }, [
    rateTableNameAndDescriptionLoad,
    requestedShippingServicePromisedDeliveryDate,
  ]);

  const rateTableName = useMemo(() => {
    if (!rateTableNameAndDescriptionLoad.value) {
      return undefined;
    }

    if (
      rateTableNameAndDescriptionLoad.value.name?.includes(
        DELIVERY_DATE_VARIABLE,
      ) &&
      requestedShippingServicePromisedDeliveryDate
    ) {
      return rateTableNameAndDescriptionLoad.value.name.replace(
        DELIVERY_DATE_VARIABLE,
        formatShortDayDate(requestedShippingServicePromisedDeliveryDate),
      );
    } else if (requestedShippingServicePromisedDeliveryDate) {
      return `Arrives by ${formatShortDayDate(requestedShippingServicePromisedDeliveryDate)}`;
    }

    return undefined;
  }, [
    rateTableNameAndDescriptionLoad,
    requestedShippingServicePromisedDeliveryDate,
  ]);

  return { rateTableDescription, rateTableName };
}
